import { Component, computed, effect, inject, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';

import { SHELL_VERSION } from '../../core/version';
import { CompanyContextService } from '../../services/company-context.service';
import { LicenseContextService } from '../../services/license-context.service';
import { ModuleRegistryService } from '../../services/module-registry.service';

/**
 * The service page of one module, at `/modules/info/:moduleId`.
 *
 * Everything support asks for in one place: which version of the module this
 * shell runs, which UMC catalogue module it is licensed under, and what the
 * active company's licence says — names first, the GUIDs in parentheses,
 * because a support call quotes the name and a database query needs the GUID.
 *
 * Reached from the licence dialog on the dashboard tile; also reachable
 * directly by URL for a link in a ticket.
 */
@Component({
  selector: 'app-module-info',
  imports: [Card, Button, RouterLink, TranslatePipe, DatePipe],
  templateUrl: './module-info.html',
  styleUrl: './module-info.scss',
})
export class ModuleInfo {
  /** Bound from the `:moduleId` route parameter. */
  readonly moduleId = input.required<string>();

  /** The shell's own version, from the workspace package.json at build time. */
  protected readonly shellVersion = SHELL_VERSION;

  private readonly registry = inject(ModuleRegistryService);
  protected readonly licenses = inject(LicenseContextService);
  protected readonly companies = inject(CompanyContextService);

  protected readonly module = computed(
    () => this.registry.modules().find((module) => module.id === this.moduleId()) ?? null,
  );

  protected readonly licenseState = computed(() => this.licenses.licenseStateOf(this.moduleId()));

  protected readonly catalog = computed(() => this.licenses.catalogEntryOf(this.moduleId()));

  protected readonly licence = computed(() => this.licenses.licenseDetailOf(this.moduleId()));

  constructor() {
    // The licence rows are fetched on demand; the company may still be loading
    // when the page is opened by URL, so the request waits for the membership.
    effect(() => {
      if (this.companies.activeMembership()) {
        this.licenses.loadLicenseDetails();
      }
    });
  }
}
